import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { AnimatePresence } from 'framer-motion';
import { SessionProvider, useSession } from 'next-auth/react';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ThemeProvider } from 'react-jss';
import React from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CiamClient } from '../client/CiamClient/CiamClient';
import { Notifications } from '../components/molecules/Notifications';
import { ClientProvider } from '../contexts/ClientContext';
import { initTranslation } from '../i18n';
import { dsoTheme, iteroTheme, resellerTheme } from '../theme';

function SessionWatcher() {
  const { status } = useSession();
  const router = useRouter();
  const isAuthRoute = router.pathname.startsWith('/auth') || router.pathname.startsWith('/api');
  React.useEffect(() => {
    if (status === 'unauthenticated' && !isAuthRoute) {
      router.replace(`/auth/signin?callbackUrl=${encodeURIComponent(router.asPath)}`);
    }
  }, [status, isAuthRoute, router]);
  return null;
}

function Page({ Component, pageProps: { lang = 'en', ...props } }: AppProps) {
  if (i18n.locale !== lang) {
    initTranslation({ i18n, locale: lang });
  }
  const queryClient = new QueryClient();
  const { businessUnit } = props;

  const [client] = React.useState(new CiamClient());
  React.useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);
  return (
    <ThemeProvider
      theme={
        businessUnit === 'exocad' || businessUnit === 'reseller'
          ? resellerTheme
          : businessUnit === 'itero'
          ? iteroTheme
          : dsoTheme
      }
    >
      <I18nProvider i18n={i18n}>
        <Notifications>
          <QueryClientProvider client={queryClient}>
            <SessionProvider session={props.session} refetchInterval={60} refetchOnWindowFocus={true}>
              <ClientProvider value={client}>
                <SessionWatcher />
                <AnimatePresence initial={false}>
                  <Component {...props} />
                </AnimatePresence>
              </ClientProvider>
            </SessionProvider>
          </QueryClientProvider>
        </Notifications>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default Page;
